import { Icon } from '@euler/components/Icomoon';
import { FontFamily } from '@euler/components/typography';
import { getDefectiveLevelColor } from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { DefectiveLevel } from '@euler/model';
import { InspectionTaskJobStatus } from '@euler/model/enum';
import { memo } from 'react';
import { StyleProp, Text, TextStyle, View, ViewStyle } from 'react-native';
import { useTheme } from 'styled-components';

export const JobDetailItemView = memo(
  ({
    title,
    status,
    defectiveLevel,
    style,
    titleStyle,
    statusStyle,
    iconSize,
    iconStyle,
  }: {
    title: string;
    defectiveLevel: DefectiveLevel;
    status: InspectionTaskJobStatus;
    style?: StyleProp<ViewStyle>;
    titleStyle?: StyleProp<TextStyle>;
    statusStyle?: StyleProp<TextStyle>;
    iconStyle?: StyleProp<ViewStyle>;
    iconSize?: number;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const color = getDefectiveLevelColor(theme, defectiveLevel);
    const finished = status === InspectionTaskJobStatus.Finished;
    return (
      <View
        css={`
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          flex-wrap: nowrap;
        `}
        style={style}
      >
        <Dot color={color} />
        <Text
          css={`
            font-family: ${FontFamily.NotoSans.Light};
            font-size: ${rpx(13)}px;
            color: ${color};
            flex: 1;
            margin-left: ${rpx(4)}px;
          `}
          style={titleStyle}
        >
          {title}
        </Text>
        <View
          css={`
            flex-direction: row;
            align-items: center;
            justify-content: flex-end;
          `}
        >
          <Icon
            name={finished ? 'checkmark-circle' : 'clock'}
            size={iconSize ?? rpx(finished ? 14 : 12)}
            color={
              finished ? theme.colors.status.success : theme.colors.status.warn
            }
            css={`
              transform: translateY(1px);
            `}
            style={iconStyle}
          />
          <Text
            css={`
              font-family: ${FontFamily.NotoSans.Light};
              font-size: ${rpx(13)}px;
              margin-left: ${rpx(2)}px;
              color: ${finished
                ? theme.colors.status.success
                : theme.colors.status.warn};
            `}
            style={statusStyle}
          >
            {finished ? '已修复' : '修复中'}
          </Text>
        </View>
      </View>
    );
  },
);

const kDotSize = 6;

const Dot = memo(
  ({
    size,
    color,
    style,
  }: {
    color: string;
    size?: number;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    size = size ?? rpx(kDotSize);
    return (
      <View
        css={`
          width: ${size}px;
          height: ${size}px;
          border-radius: ${size / 2}px;
          background-color: ${color};
        `}
        style={style}
      />
    );
  },
);
